import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { Check as CheckIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';

export type StatusTone = 'ok' | 'warning' | 'error' | 'info' | 'neutral';

const toneToColor: Record<StatusTone, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ok: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  neutral: 'default',
};

export const StatusChip: React.FC<{ label: string; tone: StatusTone }> = ({ label, tone }) => (
  <Chip label={label} size="small" variant="outlined" color={toneToColor[tone]} />
);

/** Dica curta ao lado de um botão (por exemplo, o motivo de ele estar desabilitado). */
export const StepHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: 'text.secondary', flex: '1 1 240px', minWidth: 0 }}>
    <InfoIcon sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />
    <Typography variant="caption">{children}</Typography>
  </Box>
);

interface StepSectionProps {
  number: number;
  /** done = concluída; current = próxima a fazer; off = ainda não alcançada. */
  state: 'done' | 'current' | 'off';
  title: string;
  chip: React.ReactNode;
  children: React.ReactNode;
}

/** Etapa numerada do detalhe: número (ou check), título, chip de status e corpo recuado. */
export const StepSection: React.FC<StepSectionProps> = ({ number, state, title, chip, children }) => {
  const titleId = `evo-step-${number}`;
  return (
    <Paper component="section" aria-labelledby={titleId} sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 500,
            bgcolor: state === 'done' ? 'success.main' : state === 'current' ? 'primary.main' : 'action.disabledBackground',
            color: state === 'off' ? 'text.secondary' : 'common.white',
          }}
        >
          {state === 'done' ? <CheckIcon sx={{ fontSize: 18 }} /> : number}
        </Box>
        <Typography id={titleId} variant="h6" component="h2" sx={{ flex: '1 1 160px', minWidth: 0 }}>
          {title}
        </Typography>
        {chip}
      </Box>
      <Box sx={{ ml: { xs: 0, sm: 5.5 }, mt: 2 }}>{children}</Box>
    </Paper>
  );
};
